import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { QuotationService } from "../../services/quotation/Quotation";
import { jsPDF } from "jspdf";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import {
  useClearCartMutation,
  useGetCartQuery,
  useRemoveCartItemMutation,
  useUpdateCartItemMutation
} from "../../services/api/cartApi";

const formatPrice = (price) => Number(price || 0).toLocaleString("en-NG");
const brandLogoPath = `${process.env.PUBLIC_URL || ""}/logo.png`;

const CartPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.appState);
  const { siteSettings } = useSiteSettings();
  const { data: cartResponse, isLoading: isLoadingCart } = useGetCartQuery(undefined, { skip: !isAuthenticated });
  const [updateCartItem] = useUpdateCartItemMutation();
  const [removeCartItem] = useRemoveCartItemMutation();
  const [clearCart] = useClearCartMutation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [quoteForm, setQuoteForm] = useState({
    eventDate: "",
    rentalDays: 1,
    eventLocation: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    notes: ""
  });
  const orderNowItem = location.state?.orderNowItem || null;
  const items = orderNowItem ? [orderNowItem] : cartResponse?.data?.items || [];
  const totalItems = cartResponse?.data?.totalItems || 0;
  const subtotal = cartResponse?.data?.subtotal || 0;

  useEffect(() => {
    if (orderNowItem) {
      setIsDialogOpen(true);
    }
  }, [orderNowItem]);

  const handleQuoteFormChange = (event) => {
    const { name, value } = event.target;
    setQuoteForm((current) => ({ ...current, [name]: value }));
    setFieldErrors((current) => ({
      ...current,
      [name]: ""
    }));
  };

  const generatePdf = async (quotation) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 20;

    const logoDataUrl = await fetch(brandLogoPath)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      )
      .catch(() => null);

    const addHeader = () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", pageWidth / 2 - 18, 12, 36, 18);
      }
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Gentle Rentals Quotation", pageWidth / 2, 36, { align: "center" });
      doc.setFont("helvetica", "normal");
    };

    const addFooter = () => {
      const footerY = pageHeight - 16;
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", pageWidth / 2 - 10, footerY - 8, 20, 10);
      }
      doc.setFontSize(9);
      doc.setTextColor(107, 114, 128);
      doc.text("Gentle Events", pageWidth / 2, footerY + 4, { align: "center" });
    };

    addHeader();
    currentY = 52;

    doc.setFontSize(11);
    doc.text(`Quote No: ${quotation.quoteNumber}`, 14, currentY);
    currentY += 7;
    doc.text(`Customer: ${user?.fullName || ""}`, 14, currentY);
    currentY += 7;
    doc.text(`Email: ${user?.email || ""}`, 14, currentY);
    currentY += 7;
    doc.text(`Phone: ${user?.phone || ""}`, 14, currentY);
    currentY += 7;
    doc.text(`Event Date: ${quotation.eventDate || quoteForm.eventDate || "Not specified"}`, 14, currentY);
    currentY += 7;
    doc.text(`Rental Days: ${quotation.rentalDays || quoteForm.rentalDays || 1}`, 14, currentY);
    currentY += 7;
    doc.text(`Return Date: ${quotation.returnDate || "Not specified"}`, 14, currentY);
    currentY += 7;
    doc.text(`Location: ${quotation.eventLocation || quoteForm.eventLocation || "Not specified"}`, 14, currentY);
    currentY += 7;
    doc.text(`Emergency Contact: ${quoteForm.emergencyContactName || "Not specified"}`, 14, currentY);
    currentY += 7;
    doc.text(`Emergency Phone: ${quoteForm.emergencyContactPhone || "Not specified"}`, 14, currentY);
    currentY += 10;

    doc.setFontSize(12);
    doc.text("Items", 14, currentY);
    currentY += 8;

    (quotation.items || items).forEach((item, index) => {
      const lineTotal = Number(item.lineTotal || item.unitPrice * item.quantity * Number(quotation.rentalDays || quoteForm.rentalDays || 1));
      const rentalDays = Number(quotation.rentalDays || quoteForm.rentalDays || 1);
      const line = `${index + 1}. ${item.title} - Qty ${item.quantity} x ${rentalDays} days x N${formatPrice(
        item.unitPrice
      )} = N${formatPrice(lineTotal)}`;
      if (currentY > pageHeight - 28) {
        doc.addPage();
        currentY = 20;
      }
      doc.text(line, 14, currentY);
      currentY += 7;
    });

    currentY += 4;
    doc.text(`Total Items: ${quotation.totalItems || totalItems}`, 14, currentY);
    currentY += 7;
    doc.text(`Subtotal: N${formatPrice(quotation.subtotal || subtotal)}`, 14, currentY);
    currentY += 10;

    if (quoteForm.notes) {
      doc.text("Notes:", 14, currentY);
      currentY += 7;
      const splitNotes = doc.splitTextToSize(quoteForm.notes, 180);
      doc.text(splitNotes, 14, currentY);
    }

    addFooter();
    doc.save(`${quotation.quoteNumber}.pdf`);
  };

  const handleGenerateQuotation = async () => {
    const nextFieldErrors = {};

    if (!quoteForm.eventDate) {
      nextFieldErrors.eventDate = "Event date is required.";
    }

    if (!Number.isFinite(Number(quoteForm.rentalDays)) || Number(quoteForm.rentalDays) < 1) {
      nextFieldErrors.rentalDays = "Rental days must be at least 1.";
    }

    if (!quoteForm.eventLocation.trim()) {
      nextFieldErrors.eventLocation = "Event location is required.";
    }

    if (!quoteForm.emergencyContactName.trim()) {
      nextFieldErrors.emergencyContactName = "Emergency contact name is required.";
    }

    if (!quoteForm.emergencyContactPhone.trim()) {
      nextFieldErrors.emergencyContactPhone = "Emergency contact phone is required.";
    }

    setFieldErrors(nextFieldErrors);

    if (Object.keys(nextFieldErrors).length > 0) {
      return;
    }

    try {
      setIsGenerating(true);
      setError("");
      setSuccess("");
      setFieldErrors({});

      const payload = {
        eventDate: quoteForm.eventDate,
        rentalDays: Number(quoteForm.rentalDays) || 1,
        eventLocation: quoteForm.eventLocation,
        emergencyContactName: quoteForm.emergencyContactName,
        emergencyContactPhone: quoteForm.emergencyContactPhone,
        notes: quoteForm.notes,
        items: items.map((item) => ({
          productId: item.productId,
          title: item.title,
          slug: item.slug,
          quantity: item.quantity,
          unitPrice: item.unitPrice
        }))
      };

      const response = await QuotationService.createQuotation(payload);
      await generatePdf(response.data);
      setSuccess("Quotation generated and sent to admin successfully.");
      setIsDialogOpen(false);
      setQuoteForm({
        eventDate: "",
        rentalDays: 1,
        eventLocation: "",
        emergencyContactName: "",
        emergencyContactPhone: "",
        notes: ""
      });
    } catch (quotationError) {
      setError(quotationError.message || "Unable to generate quotation.");
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 160px)",
          px: { xs: 2, md: 6 },
          py: { xs: 4, md: 6 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Paper sx={{ maxWidth: 620, p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: "center" }}>
          <ShoppingBagOutlinedIcon sx={{ fontSize: 54, color: "#f59e0b", mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
            Login to view your cart
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3 }}>
            Your cart is reserved for signed-in users so we can keep your selected rental items consistent.
          </Typography>
          <Button component={RouterLink} to="/account" variant="contained" sx={{ backgroundColor: "orange" }}>
            Go to Account
          </Button>
        </Paper>
      </Box>
    );
  }

  if (isLoadingCart) {
    return (
      <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 4, md: 6 } }}>
        <Typography sx={{ textAlign: "center", color: "text.secondary" }}>Loading your cart...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 4, md: 6 } }}>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
        Your Cart
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 4 }}>
        Review your selected rental items in real time before you continue.
      </Typography>

      {items.length === 0 ? (
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: "center" }}>
          <ShoppingBagOutlinedIcon sx={{ fontSize: 54, color: "#f59e0b", mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5 }}>
            Your cart is empty
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3 }}>
            Browse listings and add the chairs or tables you want to reserve.
          </Typography>
          <Button component={RouterLink} to="/products" variant="contained" sx={{ backgroundColor: "orange" }}>
            Browse Products
          </Button>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.5fr 0.8fr" },
            gap: 3,
            alignItems: "start"
          }}
        >
          <Stack spacing={2.5}>
            {items.map((item) => (
              <Card
                key={item.productId}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
                  borderRadius: 3,
                  overflow: "hidden"
                }}
              >
                <CardMedia component="img" image={item.imageUrl} alt={item.title} sx={{ height: { xs: 220, sm: "100%" } }} />
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }}>₦{formatPrice(item.unitPrice)} per item</Typography>
                    </Box>
                    <IconButton onClick={() => removeCartItem(item.productId)} color="error">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>

                  <Typography sx={{ color: "text.secondary", mb: 2 }}>
                    Available units: {item.quantityAvailable}
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton onClick={() => updateCartItem({ productId: item.productId, quantity: item.quantity - 1 })}>
                        <RemoveIcon />
                      </IconButton>
                      <TextField
                        type="number"
                        size="small"
                        value={item.quantity}
                        onChange={(event) =>
                          updateCartItem({
                            productId: item.productId,
                            quantity: Math.max(1, Math.min(Number(event.target.value) || 1, Number(item.quantityAvailable || 1)))
                          })
                        }
                        inputProps={{
                          min: 1,
                          max: Number(item.quantityAvailable || 1),
                          style: { textAlign: "center", padding: "8px 6px", width: 56 }
                        }}
                        sx={{
                          width: 72,
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0
                          },
                          "& input[type=number]": {
                            MozAppearance: "textfield"
                          }
                        }}
                      />
                      <IconButton
                        onClick={() => updateCartItem({ productId: item.productId, quantity: item.quantity + 1 })}
                        disabled={item.quantity >= item.quantityAvailable}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>

                    <Typography sx={{ fontWeight: 800, color: "#d97706", fontSize: "1.1rem" }}>
                      ₦{formatPrice(item.unitPrice * item.quantity)}
                    </Typography>
                  </Box>

                  <Button
                    component={RouterLink}
                    to={`/products/${item.slug}`}
                    variant="contained"
                    sx={{
                      mt: 2,
                      backgroundColor: siteSettings.addToCartColor,
                      "&:hover": { backgroundColor: siteSettings.addToCartColor }
                    }}
                  >
                    View Listing
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Stack>

          <Paper sx={{ p: 3, borderRadius: 3, position: { lg: "sticky" }, top: { lg: 110 } }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              Cart Summary
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "text.secondary" }}>Items</Typography>
                <Typography sx={{ fontWeight: 700 }}>{totalItems}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "text.secondary" }}>Subtotal</Typography>
                <Typography sx={{ fontWeight: 700 }}>₦{formatPrice(subtotal)}</Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            <Typography sx={{ color: "text.secondary", mb: 3 }}>
              This cart updates instantly as you add, remove, or adjust rental quantities.
            </Typography>

            <Stack spacing={1.5}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: siteSettings.addToCartColor,
                  "&:hover": { backgroundColor: siteSettings.addToCartColor }
                }}
                onClick={() => setIsDialogOpen(true)}
              >
                Generate Quotation
              </Button>
              <Button
                variant="outlined"
                onClick={() => clearCart()}
                sx={{
                  borderColor: siteSettings.addToCartColor,
                  color: siteSettings.addToCartColor,
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    borderColor: siteSettings.addToCartColor,
                    color: siteSettings.addToCartColor,
                    backgroundColor: "rgba(255,255,255,0.96)"
                  }
                }}
              >
                Clear Cart
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            color: "#111827",
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ color: "#111827", fontWeight: 800 }}>Generate Quotation PDF</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Event Date"
              name="eventDate"
              type="date"
              value={quoteForm.eventDate}
              onChange={handleQuoteFormChange}
              required
              error={Boolean(fieldErrors.eventDate)}
              helperText={fieldErrors.eventDate || " "}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  color: "#111827"
                },
                "& .MuiInputLabel-root": {
                  color: "#374151"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: siteSettings.addToCartColor
                }
              }}
              fullWidth
            />
            <TextField
              label="Rental Days"
              name="rentalDays"
              type="number"
              value={quoteForm.rentalDays}
              onChange={handleQuoteFormChange}
              inputProps={{ min: 1 }}
              required
              error={Boolean(fieldErrors.rentalDays)}
              helperText={fieldErrors.rentalDays || " "}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  color: "#111827"
                },
                "& .MuiInputLabel-root": {
                  color: "#374151"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: siteSettings.addToCartColor
                }
              }}
              fullWidth
            />
            <TextField
              label="Event Location"
              name="eventLocation"
              value={quoteForm.eventLocation}
              onChange={handleQuoteFormChange}
              required
              error={Boolean(fieldErrors.eventLocation)}
              helperText={fieldErrors.eventLocation || " "}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  color: "#111827"
                },
                "& .MuiInputLabel-root": {
                  color: "#374151"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: siteSettings.addToCartColor
                }
              }}
              fullWidth
            />
            <TextField
              label="Emergency Contact Name"
              name="emergencyContactName"
              value={quoteForm.emergencyContactName}
              onChange={handleQuoteFormChange}
              required
              error={Boolean(fieldErrors.emergencyContactName)}
              helperText={fieldErrors.emergencyContactName || " "}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  color: "#111827"
                },
                "& .MuiInputLabel-root": {
                  color: "#374151"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: siteSettings.addToCartColor
                }
              }}
              fullWidth
            />
            <TextField
              label="Emergency Contact Phone"
              name="emergencyContactPhone"
              value={quoteForm.emergencyContactPhone}
              onChange={handleQuoteFormChange}
              required
              error={Boolean(fieldErrors.emergencyContactPhone)}
              helperText={fieldErrors.emergencyContactPhone || " "}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  color: "#111827"
                },
                "& .MuiInputLabel-root": {
                  color: "#374151"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: siteSettings.addToCartColor
                }
              }}
              fullWidth
            />
            <TextField
              label="Additional Notes"
              name="notes"
              value={quoteForm.notes}
              onChange={handleQuoteFormChange}
              multiline
              minRows={4}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  color: "#111827"
                },
                "& .MuiInputLabel-root": {
                  color: "#374151"
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: siteSettings.addToCartColor
                }
              }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDialogOpen(false)} sx={{ color: "#111827" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerateQuotation}
            disabled={isGenerating}
            sx={{
              backgroundColor: siteSettings.addToCartColor,
              "&:hover": { backgroundColor: siteSettings.addToCartColor }
            }}
          >
            {isGenerating ? "Generating..." : "Generate & Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CartPage;
